export default function useEvent() {
  const { proxy } = useGA4()
  const { proxy: metaPixel } = useMetaPixel()
  const { proxy: clarity } = useClarity()
  const { locale } = useI18n()
  const route = useRoute()

  function sendEnabledFilter(filter: string) {
    clarity.clarity('event', 'Enabled filter')
    metaPixel.fbq('trackCustom', 'EnabledFilter', {
      filter: filter,
      locale: locale.value,
    })
    proxy.gtag('event', 'enabled_filter', {
      filter,
      locale: locale.value,
    })
  }

  function sendDisabledFilter(filter: string) {
    clarity.clarity('event', 'Disabled filter')
    metaPixel.fbq('trackCustom', 'DisabledFilter', {
      filter: filter,
      locale: locale.value,
    })
    proxy.gtag('event', 'disabled_filter', {
      filter,
      locale: locale.value,
    })
  }

  function sendProductSetView(productTitle: string) {
    clarity.clarity('event', 'Product set view')
    metaPixel.fbq('trackCustom', 'ProductSetView', {
      productTitle: productTitle,
      locale: locale.value,
    })
    proxy.gtag('event', 'product_set_view', {
      product_title: productTitle,
      locale: locale.value,
    })
  }

  function sendStartConfigurator(productTitle: string) {
    clarity.clarity('event', 'Start configurator')
    metaPixel.fbq('trackCustom', 'StartConfigurator', {
      productTitle: productTitle,
      locale: locale.value,
    })
    proxy.gtag('event', 'start_configurator', {
      product_title: productTitle,
      locale: locale.value,
    })
  }

  function sendConfigurator10PlusClicks(productTitle: string) {
    clarity.clarity('event', 'Configurator 10+ clicks')
    metaPixel.fbq('trackCustom', 'Configurator10PlusClicks', {
      productTitle: productTitle,
      locale: locale.value,
    })
    proxy.gtag('event', 'configurator_10_plus_clicks', {
      product_title: productTitle,
      locale: locale.value,
    })
  }

  function sendNewRegistrationEvent(country: string) {
    clarity.clarity('event', 'New registration')
    clarity.clarity('event', `New registration ${country}`)
    metaPixel.fbq('trackCustom', 'NewRegistration', {
      country,
      locale: locale.value,
    })
    proxy.gtag('event', 'new_registration', {
      country,
      locale: locale.value,
    })
  }

  function sendNeedHelpEvent(productTitle: string) {
    clarity.clarity('event', 'Need help')
    proxy.gtag('event', 'need_help', {
      product_title: productTitle,
      locale: locale.value,
    })
    metaPixel.fbq('trackCustom', 'ClickNeedHelp', {
      productTitle: productTitle,
      locale: locale.value,
    })
  }

  function sendNeedHelpSentEvent(productTitle: string) {
    clarity.clarity('event', 'Need help sent')
    proxy.gtag('event', 'need_help_sent', {
      product_title: productTitle,
      locale: locale.value,
    })
    metaPixel.fbq('trackCustom', 'NeedHelpSent', {
      productTitle: productTitle,
      locale: locale.value,
    })
  }

  function sendCustomiseYourChairEvent(productTitle: string) {
    clarity.clarity('event', 'Customise your chair')
    metaPixel.fbq('trackCustom', 'CustomiseYourChair', {
      productTitle: productTitle,
      locale: locale.value,
    })
    proxy.gtag('event', 'customise_your_chair', {
      product_title: productTitle,
      locale: locale.value,
    })
  }

  function sendEnquireEvent(productTitle: string, isConfigurator: boolean) {
    clarity.clarity(
      'event',
      'Enquire' + isConfigurator ? ' (from configurator)' : ''
    )
    proxy.gtag('event', 'enquire', {
      product_title: productTitle,
      locale: locale.value,
      is_configurator: isConfigurator,
    })
    metaPixel.fbq('trackCustom', 'ClickEnquire', {
      productTitle: productTitle,
      locale: locale.value,
      isConfigurator: isConfigurator,
    })
  }

  function sendEnquireSentEvent(productTitle: string, isConfigurator: boolean) {
    clarity.clarity(
      'event',
      'Enquire sent' + isConfigurator ? ' (from configurator)' : ''
    )
    proxy.gtag('event', 'enquire_sent', {
      product_title: productTitle,
      locale: locale.value,
      is_configurator: isConfigurator,
    })
    metaPixel.fbq('trackCustom', 'EnquireSent', {
      productTitle: productTitle,
      locale: locale.value,
      isConfigurator: isConfigurator,
    })
  }

  function sendSearchEvent(query: string) {
    clarity.clarity('event', 'Search')
    metaPixel.fbq('trackCustom', 'Search', {
      query,
      locale: locale.value,
      path: route.path,
      page_title: import.meta.client ? document.title : '',
    })
    proxy.gtag('event', 'search', {
      query,
      locale: locale.value,
      path: route.path,
      page_title: import.meta.client ? document.title : '',
    })
  }

  function sendSignedUpForUpdatesEvent(country: string) {
    clarity.clarity('event', 'Signed up for updates')
    clarity.clarity('event', `Signed up for updates ${country}`)
    proxy.gtag('event', 'signed_up_for_updates', {
      country,
      locale: locale.value,
      path: route.path,
      page_title: import.meta.client ? document.title : '',
    })
    metaPixel.fbq('trackCustom', 'SignedUpForUpdates', {
      country: country,
      locale: locale.value,
      path: route.path,
      page_title: import.meta.client ? document.title : '',
    })
  }

  return {
    sendEnabledFilter,
    sendDisabledFilter,
    sendProductSetView,
    sendStartConfigurator,
    sendConfigurator10PlusClicks,
    sendNewRegistrationEvent,
    sendNeedHelpEvent,
    sendNeedHelpSentEvent,
    sendCustomiseYourChairEvent,
    sendEnquireEvent,
    sendEnquireSentEvent,
    sendSearchEvent,
    sendSignedUpForUpdatesEvent,
  }
}
